/**
 * Button dropdown <uo-dropdown>
 * Figma: <Bubble>
 */

// Import utilities
import { isEmpty } from '@automator/utilities';

// Import animations
import { fadeIn, fadeOut } from '@automator/animations';

// Import style for web component
const styles = require( './web-component-style.scss' );

/**
 * Creates the <uo-dropdown> web component
 */
class AutomatorDropdown extends HTMLElement {
	/**
	 * Defines observed attributes
	 *
	 * @return {Array} Observed attributes
	 */
	static get observedAttributes() {
		return [ 'open', 'items-close-dropdown', 'align' ];
	}

	/**
	 * Renders the component
	 *
	 * @return {undefined}
	 */
	constructor() {
		super();

		// Attach shadow DOM
		this.attachShadow( {
			mode: 'open',
		} );

		// Create button
		this.shadowRoot.innerHTML = `
			<style>
				:host {
					display: inline-flex;
				}

				${styles}
			</style>

			<div class="dropdown">
				<div class="dropdown-button">
					<slot name="button"></slot>
				</div>
				<div class="dropdown-bubble"></div>
			</div>
		`;

		// Add the list with the items to the bubble
		// IMPORTANT: This will move the ul from the Light DOM to the Shadow DOM,
		// so the styles will be removed
		this.$bubble.appendChild( ...this.querySelectorAll( ':scope > ul' ) );
	}

	/**
	 * Callback invoked when the component is added to the DOM
	 *
	 * @return {undefined}
	 */
	connectedCallback() {
		// Handle toggle button
		this.#handleToggleButton();

		// Handle clicks on the items
		this.#handleClickOnItems();
	}

	/**
	 * Callback invoked when the component is removed from the DOM
	 *
	 * @return {undefined}
	 */
	disconnectedCallback() {}

	/**
	 * Callback invoked when the attribute changes
	 *
	 * @param {String} attrName Attribute name
	 * @param {String} oldValue The old value
	 * @param {String} newValue The new value
	 * @return {undefined}
	 */
	attributeChangedCallback( attrName, oldValue, newValue ) {
		// Check if the old value is different than the new one
		// This will prevent an infinite loop
		if ( oldValue !== newValue ) {
			if ( attrName == 'items-close-dropdown' ) {
				this.itemsCloseDropdown = this.hasAttribute( attrName );
			} else if ( [ 'open' ].includes( attrName ) ) {
				this[attrName] = this.hasAttribute( attrName );
			} else {
				this[attrName] = this.getAttribute( attrName );
			}
		}
	}

	/**
	 * Handles clicks made to the component
	 *
	 * @return {undefined}
	 */
	#handleToggleButton() {
		// Listen clicks to the button
		this.$toggleButton.addEventListener( 'click', () => {
			// Invert current value
			if ( this.open ) {
				this.removeAttribute( 'open' );
			} else {
				this.setAttribute( 'open', '' );
			}
		} );
	}

	/**
	 * Handle clicks made to the items/options
	 *
	 * @return {undefined}
	 */
	#handleClickOnItems() {
		// Iterate items
		this.$items.forEach( ( $item ) => {
			// Add event listener
			$item.addEventListener( 'click', () => {
				// Check if we should close the bubble
				if ( this.itemsCloseDropdown ) {
					// Make sure the item is not disabled
					if ( ! $item.hasAttribute( 'disabled' ) ) {
						// Close bubble
						this.removeAttribute( 'open' );
					}
				}

				// Communicate the click
				this.dispatchEvent(
					new CustomEvent( 'automator/dropdown/item-clicked', {
						detail: {
							item: $item,
						},
					} )
				);
			} );
		} );
	}

	/**
	 * Determines if the button is open by checking if the element has
	 * the attribute "open"
	 *
	 * @return {Boolean} True if it's open
	 */
	get open() {
		return this.hasAttribute( 'open' );
	}

	/**
	 * Sets the status of the disabled attribute
	 *
	 * @param {Boolean} value True if the dropdown should be open
	 * @return {undefined}
	 */
	set open( value ) {
		if ( value ) {
			// Set open status in the host
			this.setAttribute( 'open', '' );

			// Set open attribute to the dropdown inside
			this.$dropdown.setAttribute( 'open', '' );

			// Show the bubble
			fadeIn( this.$bubble );
		} else {
			// Set open status in the host
			this.removeAttribute( 'open' );

			// Set open attribute to the dropdown inside
			this.$dropdown.removeAttribute( 'open' );

			// Hide the bubble
			fadeOut( this.$bubble );
		}
	}

	/**
	 * Determines if the bubble should be closed when the user
	 * clicks on an option
	 *
	 * @return {Boolean} True if we should close it
	 */
	get itemsCloseDropdown() {
		return this.hasAttribute( 'items-close-dropdown' );
	}

	/**
	 * Sets the value of the attribute that determines if the
	 * bubble should be closed when the user clicks on an option
	 *
	 * @param {Boolean} value True if we should close it
	 * @return {undefined}
	 */
	set itemsCloseDropdown( value ) {
		if ( value ) {
			// Set attribute
			this.setAttribute( 'items-close-dropdown', '' );
		} else {
			// Set attrib
			this.removeAttribute( 'items-close-dropdown' );
		}
	}

	/**
	 * Determines the alignment of the bubble
	 *
	 * @return {String} The alignment of the dropdown
	 */
	get align() {
		return this.getAttribute( 'align' );
	}

	/**
	 * Sets the alignment of the bubble
	 *
	 * @param {String} value The alignment: "left" (default), "center", "right"
	 * @return {undefined}
	 */
	set align( value ) {
		// Check if the value of the attribute is valid
		if ( [ 'left', 'center', 'right' ].includes( value ) ) {
			// Set alignment in the host
			this.setAttribute( 'align', value );

			// Set alignment in dropdown
			this.$dropdown.setAttribute( 'align', value );
		} else {
			// Set alignment in the host
			this.removeAttribute( 'align' );

			// Remove alignment from dropdown
			this.$dropdown.removeAttribute( 'align' );
		}
	}

	/**
	 * Returns the Node of the dropdown
	 *
	 * @return {Node} The dropdown
	 */
	get $dropdown() {
		return this.shadowRoot.querySelector( '.dropdown' );
	}

	/**
	 * Returns the Node of the toggle button
	 *
	 * @return {Node} The button
	 */
	get $toggleButton() {
		return this.shadowRoot.querySelector( '.dropdown-button' );
	}

	/**
	 * Returns the Node of the bubble
	 *
	 * @return {Node} The bubble
	 */
	get $bubble() {
		return this.shadowRoot.querySelector( '.dropdown-bubble' );
	}

	/**
	 * Returns a NodeList with the items in the list
	 *
	 * @return {NodeList} The items
	 */
	get $items() {
		return this.shadowRoot.querySelectorAll( 'ul > li' );
	}
}

customElements.define( 'uo-dropdown', AutomatorDropdown );
